import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Image,
    Input,
    Link,
    Menu,
    MenuButton,
    MenuDivider,
    MenuItem,
    MenuList,
    Stack,
    Text
} from "@chakra-ui/react";
import React, {useContext, useState} from "react";
import {AuthContext, AuthContextI} from "./FirebaseAuth";
import {faUser} from "@fortawesome/free-solid-svg-icons/faUser";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {FcGoogle} from "react-icons/fc";

type EmailMode = "signin" | "signup";

const mapAuthError = (error: any): string => {
    const code: string | undefined = error?.code;
    switch (code) {
        case "auth/invalid-email":
            return "That email address looks invalid.";
        case "auth/user-disabled":
            return "This account has been disabled.";
        case "auth/user-not-found":
        case "auth/wrong-password":
        case "auth/invalid-credential":
            return "Email or password is incorrect.";
        case "auth/email-already-in-use":
            return "An account with that email already exists. Try signing in instead.";
        case "auth/weak-password":
            return "Password must be at least 6 characters.";
        case "auth/too-many-requests":
            return "Too many attempts. Please wait a moment and try again.";
        case "auth/network-request-failed":
            return "Network error. Check your connection and try again.";
        default:
            return error?.message || "Something went wrong. Please try again.";
    }
};

const AuthButton = () => {
    const authContext: AuthContextI = useContext<AuthContextI>(AuthContext);
    const user = authContext.getAuthUser();

    const [mode, setMode] = useState<EmailMode>("signin");
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [infoMessage, setInfoMessage] = useState<string | null>(null);
    const [isSubmitting, setSubmitting] = useState<boolean>(false);

    const resetMessages = () => {
        setErrorMessage(null);
        setInfoMessage(null);
    };

    const handleEmailSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        resetMessages();
        if (!email || !password) {
            setErrorMessage("Enter an email and password.");
            return;
        }
        setSubmitting(true);
        try {
            if (mode === "signin") {
                await authContext.signInWithEmail(email.trim(), password);
            } else {
                await authContext.signUpWithEmail(email.trim(), password);
            }
        } catch (error) {
            console.error("Email auth failed", error);
            setErrorMessage(mapAuthError(error));
        } finally {
            setSubmitting(false);
        }
    };

    const handleForgotPassword = async () => {
        resetMessages();
        if (!email) {
            setErrorMessage("Enter your email above, then tap \"Forgot password\" again.");
            return;
        }
        setSubmitting(true);
        try {
            await authContext.sendPasswordReset(email.trim());
            setInfoMessage(`Password reset email sent to ${email.trim()}.`);
        } catch (error) {
            console.error("Password reset failed", error);
            setErrorMessage(mapAuthError(error));
        } finally {
            setSubmitting(false);
        }
    };

    const toggleMode = () => {
        resetMessages();
        setMode((current) => (current === "signin" ? "signup" : "signin"));
    };

    return <>
        {
            !user &&
            <Flex minH={"100dvh"} align={"center"} justify={"center"} bg={"gray.50"} px={4} py={6}>
                <Box
                    w={"100%"}
                    maxW={"420px"}
                    borderRadius={"xl"}
                    overflow={"hidden"}
                    borderWidth={"1px"}
                    borderColor={"gray.200"}
                    bg={"white"}
                    boxShadow={"lg"}
                >
                    <Box px={6} py={6} bgGradient={"linear(to-r, teal.500, blue.500)"} color={"white"}>
                        <Flex alignItems={"center"} justifyContent={"space-between"} gap={4}>
                            <Box>
                                <Text fontSize={"xs"} letterSpacing={"0.3em"} textTransform={"uppercase"}>
                                    SoundSpots
                                </Text>
                                <Heading size={"md"} mt={2}>
                                    {mode === "signin" ? "Sign in" : "Create account"}
                                </Heading>
                                <Text fontSize={"sm"} mt={1} opacity={0.9}>
                                    Access your performances and sound spots.
                                </Text>
                            </Box>
                            <Image src={"/branding/logo.svg"} alt={"SoundSpots"} height={"64px"} />
                        </Flex>
                    </Box>
                    <Box px={6} py={6}>
                        <Stack spacing={3}>
                            <Button
                                size={"lg"}
                                variant={"outline"}
                                justifyContent={"flex-start"}
                                leftIcon={<FcGoogle />}
                                onClick={authContext.signIn}
                                isDisabled={isSubmitting}
                            >
                                Continue with Google
                            </Button>
                        </Stack>

                        <HStack my={5} spacing={3}>
                            <Divider />
                            <Text fontSize={"xs"} color={"gray.500"} whiteSpace={"nowrap"}>
                                or use email
                            </Text>
                            <Divider />
                        </HStack>

                        <form onSubmit={handleEmailSubmit}>
                            <Stack spacing={3}>
                                <FormControl isRequired>
                                    <FormLabel fontSize={"sm"}>Email</FormLabel>
                                    <Input
                                        type={"email"}
                                        value={email}
                                        onChange={(event) => setEmail(event.target.value)}
                                        autoComplete={"email"}
                                        isDisabled={isSubmitting}
                                    />
                                </FormControl>
                                <FormControl isRequired>
                                    <FormLabel fontSize={"sm"}>Password</FormLabel>
                                    <Input
                                        type={"password"}
                                        value={password}
                                        onChange={(event) => setPassword(event.target.value)}
                                        autoComplete={mode === "signin" ? "current-password" : "new-password"}
                                        isDisabled={isSubmitting}
                                    />
                                </FormControl>

                                {errorMessage ? (
                                    <Alert status={"error"} borderRadius={"md"} fontSize={"sm"}>
                                        <AlertIcon />
                                        {errorMessage}
                                    </Alert>
                                ) : null}
                                {infoMessage ? (
                                    <Alert status={"success"} borderRadius={"md"} fontSize={"sm"}>
                                        <AlertIcon />
                                        {infoMessage}
                                    </Alert>
                                ) : null}

                                <Button
                                    type={"submit"}
                                    colorScheme={"teal"}
                                    size={"lg"}
                                    isLoading={isSubmitting}
                                    loadingText={mode === "signin" ? "Signing in" : "Creating account"}
                                >
                                    {mode === "signin" ? "Sign in" : "Create account"}
                                </Button>

                                <Flex justify={"space-between"} fontSize={"sm"}>
                                    <Link
                                        color={"teal.600"}
                                        onClick={toggleMode}
                                    >
                                        {mode === "signin" ? "Create account" : "Have an account? Sign in"}
                                    </Link>
                                    {mode === "signin" ? (
                                        <Link
                                            color={"teal.600"}
                                            onClick={handleForgotPassword}
                                        >
                                            Forgot password?
                                        </Link>
                                    ) : null}
                                </Flex>
                            </Stack>
                        </form>

                        <Text fontSize={"xs"} color={"gray.500"} mt={5}>
                            You will stay signed in on this device.
                        </Text>
                    </Box>
                </Box>
            </Flex>
        }

        {
            user && <>
                <Menu>
                    {({isOpen}) => (
                        <>
                            <MenuButton isActive={isOpen} as={Button} rightIcon={
                                <FontAwesomeIcon
                                    enableBackground={0}
                                    icon={faUser}
                                />
                            }
                                        style={{
                                            backgroundColor: "transparent",
                                        }}
                            />
                            <MenuList zIndex={20}>
                                <MenuItem onClick={authContext.signOut}>Sign out</MenuItem>
                                <MenuDivider/>
                                <MenuItem>
                                    <Text fontSize={"xs"}>
                                        {authContext.getAuthUser()?.displayName || authContext.getAuthUser()?.email}
                                    </Text>
                                </MenuItem>
                            </MenuList>
                        </>
                    )}
                </Menu>
            </>
        }
    </>
}

export default AuthButton
